import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowUpRight, ShieldCheck, Users } from "lucide-react";
import { api, API, COLORS } from "@/lib/api";
import { OTROS_CONSEJOS } from "@/data/consejos";

const initials = (name) =>
  (name || "").split(" ").filter(Boolean).slice(0, 2).map((n) => n[0]).join("").toUpperCase();

const photoSrc = (ref) => (!ref ? null : ref.startsWith("/") ? ref : `${API}/media/${ref}`);

const fade = {
  hidden: { opacity: 0, y: 20 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.45, delay: i * 0.08 } }),
};

const NuestrosConsejos = () => {
  const [team, setTeam] = useState([]);
  useEffect(() => {
    api.get("/team").then((r) => setTeam(r.data)).catch(() => {});
  }, []);

  const directoras = team.filter((m) => m.section === "directoras");
  const juvenil = team.filter((m) => m.section === "consejo-juvenil");
  const equipo = team.filter((m) => m.section === "equipo-general");

  return (
    <div data-testid="subpage-nuestros-consejos" className="max-w-5xl mx-auto px-6 md:px-12 py-16">
      <Link to="/conocenos" data-testid="subpage-back" className="inline-flex items-center gap-2 text-sm font-semibold text-brand-ink/60 hover:text-brand-ink mb-8">
        <ArrowLeft size={16} /> Conócenos
      </Link>

      <motion.div initial="hidden" animate="show" variants={fade}>
        <span className="inline-block w-16 h-1.5 rounded-sm mb-6" style={{ background: COLORS.magenta }} />
        <h1 className="font-heading text-4xl md:text-6xl font-bold tracking-tight text-brand-ink">Nuestros consejos</h1>
        <p className="mt-5 text-lg md:text-xl text-brand-ink/70 leading-relaxed max-w-2xl">
          Los órganos que guían, acompañan y sostienen el trabajo de Juventudes Latentes.
        </p>
      </motion.div>

      {/* Consejo Ejecutivo */}
      <section className="mt-16">
        <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-brand-ink">Consejo Ejecutivo</h2>
        <p className="mt-2 text-brand-ink/70">Conoce a nuestras tres codirectoras.</p>

        <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-6">
          {directoras.map((d, i) => (
            <motion.div key={d.slug} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade}>
              <Link
                to={`/conocenos/nuestros-consejos/${d.slug}`}
                data-testid={`directora-card-${d.slug}`}
                className="group block bg-white border border-[#E5E5E5] rounded-sm overflow-hidden transition-all duration-300 hover:-translate-y-1"
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = d.color)}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E5E5E5")}
              >
                <div className="aspect-[3/4] overflow-hidden bg-[#F0F0F0]">
                  {photoSrc(d.photo_ref) ? (
                    <img src={photoSrc(d.photo_ref)} alt={d.name} style={{ objectPosition: d.image_pos || "top" }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: d.color }}>
                      <span className="font-heading text-5xl font-bold text-white/90">{initials(d.name)}</span>
                    </div>
                  )}
                </div>
                <div className="p-5">
                  <span className="inline-block w-8 h-1 rounded-sm mb-3" style={{ background: d.color }} />
                  <h3 className="font-heading text-xl font-semibold tracking-tight text-brand-ink">{d.name}</h3>
                  <p className="mt-1 text-sm text-brand-ink/60 leading-snug">{d.role}</p>
                  <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-ink group-hover:gap-2 transition-all">
                    Ver semblanza <ArrowUpRight size={16} />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Otros consejos */}
      <section className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-6">
        {OTROS_CONSEJOS.map((c, i) => {
          const Icon = i === 0 ? ShieldCheck : Users;
          return (
            <motion.div key={c.title} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i} variants={fade} className="bg-white border border-[#E5E5E5] rounded-sm p-8">
              <span className="w-12 h-12 rounded-sm flex items-center justify-center" style={{ background: c.color }}>
                <Icon size={22} className="text-white" strokeWidth={2.2} />
              </span>
              <h3 className="mt-5 font-heading text-2xl font-semibold tracking-tight text-brand-ink">{c.title}</h3>
              <p className="mt-3 text-brand-ink/70 leading-relaxed">{c.text}</p>
            </motion.div>
          );
        })}
      </section>

      {/* Consejo Juvenil */}
      <section className="mt-20">
        <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-brand-ink">Consejo Juvenil</h2>
        <p className="mt-2 text-brand-ink/70 max-w-3xl">
          Nuestras consejeras de la 3ª edición del Consejo Juvenil (edición 2025-2026). Son parte
          del diseño del plan estratégico de la organización y de la toma de decisiones, y forman
          parte de nuestra alianza <span className="font-semibold text-brand-ink">Juntes</span>.
          Conoce más sobre esta alianza en la sección de{" "}
          <Link to="/conocenos/alianzas" data-testid="juvenil-alianzas-link" className="font-semibold text-brand-magenta hover:underline">Alianzas</Link>.
        </p>

        <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
          {juvenil.map((m, i) => (
            <motion.div key={m.slug} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 4} variants={fade}>
              <Link
                to={`/conocenos/nuestros-consejos/juvenil/${m.slug}`}
                data-testid={`juvenil-card-${m.slug}`}
                className="group block bg-white border border-[#E5E5E5] rounded-sm overflow-hidden transition-all duration-300 hover:-translate-y-1"
                onMouseEnter={(e) => (e.currentTarget.style.borderColor = m.color)}
                onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E5E5E5")}
              >
                <div className="aspect-square overflow-hidden bg-[#F0F0F0]">
                  {photoSrc(m.photo_ref) ? (
                    <img src={photoSrc(m.photo_ref)} alt={m.name} style={{ objectPosition: m.image_pos || "top" }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center" style={{ background: m.color }}>
                      <span className="font-heading text-4xl font-bold text-white/90">{initials(m.name)}</span>
                    </div>
                  )}
                </div>
                <div className="p-4">
                  <h3 className="font-heading text-base font-semibold tracking-tight text-brand-ink leading-tight group-hover:text-brand-magenta transition-colors">{m.name}</h3>
                  <p className="mt-1 text-xs text-brand-ink/60 leading-snug line-clamp-2">{m.role}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Equipo General */}
      {equipo.length > 0 && (
        <section className="mt-20">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold tracking-tight text-brand-ink">Equipo General</h2>
          <p className="mt-2 text-brand-ink/70 max-w-3xl">Las personas que sostienen el día a día de Juventudes Latentes.</p>

          <div className="mt-8 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5" data-testid="equipo-general-grid">
            {equipo.map((m, i) => (
              <motion.div key={m.slug} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 4} variants={fade}>
                <Link
                  to={`/conocenos/nuestros-consejos/equipo/${m.slug}`}
                  data-testid={`equipo-card-${m.slug}`}
                  className="group block bg-white border border-[#E5E5E5] rounded-sm overflow-hidden transition-all duration-300 hover:-translate-y-1"
                  onMouseEnter={(e) => (e.currentTarget.style.borderColor = m.color)}
                  onMouseLeave={(e) => (e.currentTarget.style.borderColor = "#E5E5E5")}
                >
                  <div className="aspect-square overflow-hidden bg-[#F0F0F0]">
                    {photoSrc(m.photo_ref) ? (
                      <img src={photoSrc(m.photo_ref)} alt={m.name} style={{ objectPosition: m.image_pos || "top" }} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center" style={{ background: m.color }}>
                        <span className="font-heading text-4xl font-bold text-white/90">{initials(m.name)}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-base font-semibold tracking-tight text-brand-ink leading-tight group-hover:text-brand-magenta transition-colors">{m.name}</h3>
                    {m.role && <p className="mt-1 text-xs text-brand-ink/60 leading-snug line-clamp-2">{m.role}</p>}
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default NuestrosConsejos;
